import React, { lazy, Suspense } from "react";

import StaffAttendanceApp2 from "@/assets/staffApp/waggex-online-leave-attendance-management-india600.webp?w=400&format=webp";
import StaffAttendanceApp2desktop from "@/assets/staffApp/waggex-online-leave-attendance-management-india600.webp?w=350&format=webp";
import StaffAttendanceApp3 from "@/assets/staffApp/waggex-automatic-payslip-generator-payroll-records600.webp?w=400&format=webp";
import StaffAttendanceApp3desktop from "@/assets/staffApp/waggex-automatic-payslip-generator-payroll-records600.webp?w=350&format=webp";

import mobile360 from "@/assets/staffApp/waggex-staff-attendance-app-android-india600.webp?w=360&format=webp";
import mobile480 from "@/assets/staffApp/waggex-staff-attendance-app-android-india600.webp?w=480&format=webp";
import desktop740 from "@/assets/staffApp/waggex-staff-attendance-app-android-india600.webp?w=740&format=webp";
import desktop740_2x from "@/assets/staffApp/waggex-staff-attendance-app-android-india600.webp?w=1480&format=webp";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const HomeFaq = lazy(() => import("../pages/HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
import StatsSection from "./common/StatsSection";
import StaffAppFeatures from "./staffapp/StaffAppFeatures";
import WorkflowSection from "./staffapp/WorkflowSection";
import WhyBusinessesLove from "./staffapp/WhyBusinessesLove";
import {
  Clock3,
  WalletCards,
  CalendarDays,
  FileText,
  ShieldCheck,
  CalendarClock,
  MapPinned,
  Bell,
  ReceiptText,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const staffAttendanceFaqs1 = [
  {
    title: "What is a staff attendance app and how does Waggex work?",
    content:
      "Waggex is a mobile staff attendance app that lets employees mark GPS-verified attendance, apply for leave, check payslips, and manage shifts all from one app. Attendance data syncs directly with payroll, so your HR team never has to reconcile records manually at month end.",
  },
  {
    title: "Does the app work for field staff and remote employees in India?",
    content:
      "Yes. Waggex is built for India's on-the-ground workforce. Field staff, delivery teams, and remote employees mark attendance from any location with GPS coordinates locked to every punch. Managers see all check-ins live on the dashboard, no follow-up calls needed.",
  },
  {
    title: "How does Waggex connect attendance data with payroll?",
    content:
      "Every attendance punch flows directly into the Waggex payroll engine in real time. Working days, late arrivals, early exits, approved leave, and overtime are all calculated automatically so payroll runs without a single manual entry from your HR team.",
  },
];

const staffAttendanceFaqs2 = [
  {
    title: "Is this app a replacement for a biometric attendance system?",
    content:
      "Yes. Waggex replaces traditional biometric attendance machines with AI-powered selfie attendance and GPS verification on any Android phone. You get the same fraud-proof accuracy as a biometric attendance system without the hardware cost, maintenance, or downtime.",
  },
  {
    title: "Can employees access payslips and leave details from the app?",
    content:
      "Absolutely. Every employee gets their own self-service access through the Waggex Staff App. They can view payslips, download PDFs, check leave balances, apply for time off, and track approval status without raising a single HR ticket or making a call.",
  },
  {
    title: "Does Waggex support shift scheduling and holiday calendars?",
    content:
      "Yes. HR managers configure shift rosters, assign employees to shifts, set up rotations, and define holiday calendars directly in Waggex. Shift and holiday data feeds into attendance and payroll automatically with overtime and allowances calculated without any manual work.",
  },
];

const appfeatures = [
  {
    icon: <MapPinned size={24} aria-hidden="true" />,
    title: "GPS Attendance Tracking",
    desc: "Mark GPS-verified attendance from your office, field site, or any location on any Android phone",
  },
  {
    icon: <ReceiptText size={24} aria-hidden="true" />,
    title: "Instant Payslip Access",
    desc: "View and download monthly payslips directly from the app no HR call or email required",
  },
  {
    icon: <CalendarDays size={24} aria-hidden="true" />,
    title: "Smart Leave Management",
    desc: "Apply for leave, track approval status and check leave balance directly from your mobile app",
  },
  {
    icon: <FileText size={24} aria-hidden="true" />,
    title: "Instant Document Access",
    desc: "Access company documents and official records anytime, directly from the app quickly and securely",
  },
  {
    icon: <CalendarClock size={24} aria-hidden="true" />,
    title: "Shift Scheduling",
    desc: "View your shift roster, check schedule updates and plan your week without calling HR",
  },
  {
    icon: <Bell size={24} aria-hidden="true" />,
    title: "Real-Time Notifications",
    desc: "Get instant alerts for leave approvals, payslip releases, shift changes and reminders",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Staff Attendance App",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Android, iOS",
    description:
      "Waggex is a mobile staff attendance app for Indian businesses — GPS attendance tracking, selfie check-in, leave management, instant payslips, shift scheduling and payroll sync in one app.",

    url: "https://www.waggex.com/staff-attendance-app",

    screenshot:
      "https://www.waggex.com/waggex-staff-attendance-mobile-app-dashboard.webp",

    featureList: [
      "Staff attendance app India",
      "GPS attendance tracking",
      "Self attendance app",
      "Biometric attendance replacement",
      "Employee attendance app",
      "Leave management app India",
      "Instant payslip access",
      "Shift scheduling app",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free trial available",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "324",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a staff attendance app and how does Waggex work?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex is a mobile staff attendance app that lets employees mark GPS-verified attendance, apply for leave, check payslips, and manage shifts — all from one app with automatic payroll sync.",
        },
      },
      {
        "@type": "Question",
        name: "Does the app work for field staff and remote employees in India?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Field staff and remote employees can mark GPS-based attendance from any location, while managers track all check-ins in real time.",
        },
      },
      {
        "@type": "Question",
        name: "How does Waggex connect attendance data with payroll?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Attendance data including working days, overtime, leave, and deductions is automatically synced into payroll without manual entry.",
        },
      },
      {
        "@type": "Question",
        name: "Is this app a replacement for a biometric attendance system?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex replaces biometric machines with AI selfie and GPS attendance on mobile devices, removing hardware dependency.",
        },
      },
      {
        "@type": "Question",
        name: "Can employees access payslips and leave details from the app?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Employees can view payslips, apply for leave, and check attendance history directly from the app.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex support shift scheduling and holiday calendars?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. HR can manage shifts, rotations, and holidays, which automatically reflect in attendance and payroll.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides a mobile staff attendance app and complete HRMS for Indian businesses — GPS attendance, payroll, leave management, and HR automation.",
    areaServed: "IN",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  },
];
export default function StaffAttendanceApp() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>Employee Attendance App with GPS Tracking & Payroll</title>
        <meta
          name="description"
          content="Track employee attendance, working hours, shifts, and GPS locations in real time while automating payroll, salary calculations, and payslip generation from one platform."
        />
        <meta
          name="keywords"
          content="staff attendance app,attendance app India,self attendance app,employee attendance app India,biometric attendance system,mobile attendance app India,GPS attendance tracking app,leave management app India,payslip app for employees,shift scheduling app India,selfie attendance app"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/staff-attendance-app"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-block border border-[#1d3b5c] rounded-xl px-5 py-2 text-[#10324F] text-xs sm:text-sm font-medium">
                Staff Attendance App
              </div>

              {/* Heading */}
              <h1 className="mt-8 text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px] min-h-18">
                Top Smart Employee Attendance
                <br /> App With Payroll
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto">
                Track GPS attendance, manage leave requests, access payslips,
                and handle employee management all from one powerful staff
                attendance app built for India
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#16395f] hover:bg-[#0f2d4c] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Trial
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#1d3b5c] text-[#1d3b5c] hover:bg-[#1d3b5c] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>

              {/* Features */}
              <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-3 text-[#1d3b5c] text-xs sm:text-sm">
                {[
                  "GPS Attendance",
                  "HRMS Integrated",
                  "Works 24/7",
                  "Payroll sync",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#10324F]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}

            {/* Phones Image */}
            <picture>
              <source media="(max-width: 639px)" srcSet={mobile360} />
              <source
                media="(min-width: 640px)"
                srcSet={`${mobile480} 480w, ${desktop740} 740w, ${desktop740_2x} 1480w`}
                sizes="(max-width: 1023px) 50vw, 480px"
              />

              <div className="relative flex justify-center items-center min-h-100">
                <img
                  src={mobile480} // fallback for browsers that ignore <picture> entirely
                  width={480}
                  height={386}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Waggex staff attendance app on Android showing GPS check-in, leave management and payslip access for Indian employees"
                  className="relative object-contain"
                />
              </div>
            </picture>

            {/* <picture>
              <source media="(max-width: 639px)" srcSet={mobile360} />
              <div className="relative flex justify-center items-center min-h-100">
                <img
                  src={mobile480}
                  sizes="(max-width:640px) 100vw"
                  width={480}
                  height={386}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Waggex staff attendance app on Android showing GPS check-in, leave management and payslip access for Indian employees"
                  className="relative object-contain"
                />{" "}
              </div>
            </picture> */}
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>

      <StaffAppFeatures
        title="Everything Employees Need In One App"
        subtitle="Mark attendance, apply for leave, access payslips and stay updated."
        features={appfeatures}
      />
      {/* Row 2 */}
      <section className="PayrollWebApp bg-[#F6F9FB] w-full  py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 flex justify-center h-100">
              <img
                src={StaffAttendanceApp2}
                sizes="(max-width: 640px) 100vw"
                width="351"
                height="400"
                fetchPriority="high"
                alt="Waggex online leave and attendance management system showing unified tracking and smart leave workflow India"
                className="w-full object-contain block sm:hidden"
              />
              <img
                src={StaffAttendanceApp2desktop}
                sizes="(max-width: 640px) 100vw"
                width="351"
                height="400"
                fetchPriority="high"
                alt="Waggex online leave and attendance management system showing unified tracking and smart leave workflow India"
                className="w-full object-contain hidden sm:block"
              />
            </div>

            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading">
                  Attendance & Leave Management Made Simple
                </h2>

                <p
                  className="service-list-item-text py-4
  "
                >
                  Tracking attendance on WhatsApp and approving leave on Excel
                  creates costly errors every month. Waggex connects attendance,
                  leave, and shift management into one clean automated system.
                </p>
                <p
                  className="service-list-item-text pb-4
  "
                >
                  Every check-in, leave request, and shift change updates across
                  your entire HR dashboard in real time. Your managers approve
                  leaves from their phone. Your HR team stops chasing employees
                  for attendance data. And payroll processes at month end
                  without corrections.
                </p>

                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Unified Attendance Tracking GPS and selfie check-ins from
                      any location, synced to your dashboard live
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Smart Leave Workflows employees apply on the app, managers
                      approve instantly, balances update in real time
                    </span>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Shift & Holiday Scheduling build rosters, add holidays,
                      and manage rotations without spreadsheets
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Row 3 */}
      <section className="bg-[#FFFFFF] w-full py-10 px-6 min-h-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-1 md:order-1 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading">
                  Smart Payslip Automation & Payroll Management
                </h2>

                <p className="service-list-item-text py-4">
                  Preparing payslips manually every month wastes hours and leads
                  to salary errors. Waggex generates accurate payslips for your
                  whole team in one click PF, ESI, and TDS included
                </p>
                <p className="service-list-item-text pb-4">
                  Every salary slip is professionally formatted, legally
                  compliant, and ready to share as a PDF. Employees access their
                  payslips directly through the staff app the moment payroll is
                  processed. No printing, no distributing, no employee queries
                  about their salary breakdown
                </p>
                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      One-Click Payslip Generation process payroll and generate
                      all payslips automatically in seconds
                    </span>
                  </li>
                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Bulk PDF Distribution payslips delivered to every
                      employee's app the moment payroll runs
                    </span>
                  </li>

                  <li className="flex gap-3  items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <span className="service-list-item-text">
                      Centralised Salary Records all payroll history stored
                      securely, accessible any time for audits
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            {/* Row 4 */}
            <div className="order-2 md:order-2 flex justify-center h-100">
              <img
                src={StaffAttendanceApp3}
                sizes="(max-width: 640px) 100vw"
                width="362"
                height="400"
                fetchPriority="high"
                alt="Waggex automatic payslip generator showing one-click payslip generation and centralised salary records India"
                className="w-full object-contain block sm:hidden  "
              />
              <img
                src={StaffAttendanceApp3desktop}
                sizes="(max-width: 640px) 100vw"
                width="363"
                height="400"
                fetchPriority="high"
                alt="Waggex automatic payslip generator showing one-click payslip generation and centralised salary records India"
                className="w-full object-contain hidden sm:block"
              />
            </div>
          </div>
        </div>
      </section>
      <WorkflowSection />
      <WhyBusinessesLove />

      <Suspense fallback={null}>
        <CustomerReviews />

        <HomeFaq
          faq1={staffAttendanceFaqs1}
          faq2={staffAttendanceFaqs2}
          subtitle="Everything you want to know about the Waggex staff attendance app"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner />
        <AppDownloadCard />
        <ModernizeHR
          heading="Ready to Ditch Manual HR?"
          description="Join 10,000+ Indian businesses that track attendance, run payroll, and manage their entire team on Waggex without the chaos."
        />
      </Suspense>
    </>
  );
}
